import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Select, MenuItem, FormControl, InputLabel, InputAdornment,
  CircularProgress, Stack, Tooltip, Skeleton, useMediaQuery, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PersonIcon from '@mui/icons-material/Person';
import { getPrescriptions, getDoctors, downloadPrescriptionPdf, logPatientMessage } from '../services/api';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { useNotification } from '../hooks/useNotification';
import { useNavigate } from 'react-router-dom';
import { STATUS_RX, fmtDate, openWhatsApp } from '../components/patient-profile/patientProfileHelpers';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

// Lista de medicamentos de una receta, dentro de la fila/card expandida.
// En mobile una tabla de 5 columnas (Medicamento/Conc./Forma/Cantidad/
// Indicaciones) se comprime ilegible — se reemplaza por una lista de
// líneas, mismo criterio que el resto de la app (Patients.js, Audit.js).
const MedicationList = ({ items, isMobile }) => {
  if (!isMobile) {
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Medicamento</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Conc.</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Forma</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Cantidad</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Indicaciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.id}>
              <TableCell><Typography variant="body2" fontWeight={600}>{item.drug_name}</Typography></TableCell>
              <TableCell><Typography variant="body2">{item.concentration || '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{item.pharmaceutical_form || '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{item.quantity || '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{item.instructions || '—'}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }
  return (
    <Stack spacing={1}>
      {items.map(item => (
        <Box key={item.id} sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none', pb: 0 } }}>
          <Typography variant="body2" fontWeight={600}>
            {item.drug_name}{item.concentration ? ` ${item.concentration}` : ''}
          </Typography>
          {item.pharmaceutical_form && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.pharmaceutical_form}</Typography>
          )}
          {item.quantity && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Cantidad: {item.quantity}</Typography>
          )}
          {item.instructions && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.instructions}</Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
};

const Recetas = () => {
  const { features, loaded } = useClinicFeatures();
  const notify = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [doctors, setDoctors] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterDoctor, setFilterDoctor] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  const prescriptionsEnabled = loaded && features?.prescriptions;

  useEffect(() => {
    getDoctors({ limit: 500 }).then(r => setDoctors(r.data?.items || [])).catch(() => setDoctors([]));
  }, []);

  const load = useCallback(async () => {
    if (!prescriptionsEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await getPrescriptions({
        doctor_id: filterDoctor || undefined,
        status: filterStatus || undefined,
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      });
      setRows(res.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [prescriptionsEnabled, filterDoctor, filterStatus, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const filteredRows = search.trim()
    ? rows.filter(rx => rx.patient?.name?.toLowerCase().includes(search.trim().toLowerCase()))
    : rows;

  const doctorById = (id) => doctors.find(d => d.id === id);

  const handlePdf = async (rx) => {
    try {
      const res = await downloadPrescriptionPdf(rx.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      notify(err.userMessage || 'No se pudo generar el PDF', 'error');
    }
  };

  const handleWhatsAppRx = async (rx) => {
    if (!rx.patient?.phone) {
      notify('El paciente no tiene número de teléfono registrado', 'warning');
      return;
    }
    const verifyUrl = `${window.location.origin}/verify?token=${encodeURIComponent(rx.signature)}`;
    const items = rx.items.map(i => `• ${i.drug_name}${i.concentration ? ` ${i.concentration}` : ''}${i.instructions ? ` — ${i.instructions}` : ''}`).join('\n');
    const text = `Hola ${rx.patient.first_name || rx.patient.name}, te envío tu receta digital del ${fmtDate(rx.created_at)}.\n\n${items}\n\nVerificá tu receta en: ${verifyUrl}`;
    openWhatsApp(rx.patient.phone, text);
    try {
      await logPatientMessage(rx.patient.id, {
        channel: 'whatsapp',
        message_type: 'receta',
        message_preview: `Receta del ${fmtDate(rx.created_at)} — ${rx.items.map(i => i.drug_name).join(', ')}`,
      });
    } catch {}
    notify('WhatsApp abierto', 'success');
  };

  if (!loaded) {
    return (
      <Box>
        <Skeleton variant="text" width={220} height={40} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width={320} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={320} />
      </Box>
    );
  }

  if (!prescriptionsEnabled) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="text.secondary" gutterBottom>
          El módulo de recetas electrónicas no está habilitado.
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Activalo desde Configuración → Módulos.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>Recetas electrónicas</Typography>
        <Typography variant="body2" color="text.secondary">
          Todas las recetas emitidas en la clínica. Para crear una receta nueva, entrá al perfil del paciente correspondiente.
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            placeholder="Buscar por paciente…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Médico</InputLabel>
            <Select
              value={filterDoctor}
              label="Médico"
              onChange={e => { setPage(0); setFilterDoctor(e.target.value); }}
            >
              <MenuItem value="">Todos</MenuItem>
              {doctors.map(d => <MenuItem key={d.id} value={d.id}>Dr. {d.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={filterStatus}
              label="Estado"
              onChange={e => { setPage(0); setFilterStatus(e.target.value); }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="borrador">Borrador</MenuItem>
              <MenuItem value="emitida">Emitida</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : filteredRows.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">Sin recetas para los filtros seleccionados.</Typography>
        </Paper>
      ) : isMobile ? (
        // Mismo patrón de card que RecetasTab.js (perfil de paciente) — acá
        // además se agrega el nombre del paciente, que ahí no hace falta
        // por estar scoped a uno solo.
        <Stack spacing={1.5}>
          {filteredRows.map(rx => {
            const st = STATUS_RX[rx.status] || STATUS_RX.borrador;
            const isOpen = expanded === rx.id;
            return (
              <Paper key={rx.id} variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, rowGap: 0.5, flexWrap: 'wrap', px: 1.5, py: 1.25, cursor: 'pointer' }}
                  onClick={() => setExpanded(isOpen ? null : rx.id)}
                >
                  <Chip label={st.label} size="small"
                    sx={{ bgcolor: st.color, color: 'white', fontWeight: 700, flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                    {fmtDate(rx.created_at)}
                  </Typography>
                  <ExpandMoreIcon sx={{
                    ml: 'auto', fontSize: 18, color: 'text.disabled',
                    transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none',
                  }} />
                  <Box sx={{ width: '100%' }}>
                    <Button
                      size="small"
                      startIcon={<PersonIcon fontSize="small" />}
                      onClick={e => { e.stopPropagation(); navigate(`/patients/${rx.patient_id}`); }}
                      sx={{ textTransform: 'none', pl: 0 }}
                    >
                      {rx.patient?.name || `Paciente #${rx.patient_id}`}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {doctorById(rx.doctor_id)?.name ? `Dr. ${doctorById(rx.doctor_id).name}` : '—'} · {rx.items.length} med.
                  </Typography>
                  {(rx.status === 'emitida' || rx.status === 'vencida') && (
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                      {features?.whatsapp && (
                        <Tooltip title="Enviar receta por WhatsApp">
                          <IconButton size="small" sx={{ color: '#25D366' }}
                            onClick={e => { e.stopPropagation(); handleWhatsAppRx(rx); }}>
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Imprimir / descargar PDF">
                        <IconButton size="small"
                          onClick={e => { e.stopPropagation(); handlePdf(rx); }}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                {isOpen && (
                  <Box sx={{ px: 1.5, pb: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    {rx.diagnosis_hint && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                        <strong>Diagnóstico:</strong> {rx.diagnosis_hint}
                      </Typography>
                    )}
                    {rx.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        <strong>Notas:</strong> {rx.notes}
                      </Typography>
                    )}
                    <MedicationList items={rx.items} isMobile />
                  </Box>
                )}
              </Paper>
            );
          })}
          <TablePagination
            component={Paper}
            count={-1}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelDisplayedRows={({ from, to }) => `${from}–${to}`}
          />
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Fecha</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Médico</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Medicamentos</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map(rx => {
                const st = STATUS_RX[rx.status] || STATUS_RX.borrador;
                const isOpen = expanded === rx.id;
                return (
                  <React.Fragment key={rx.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded(isOpen ? null : rx.id)}>
                      <TableCell sx={{ width: 32 }}>
                        <ExpandMoreIcon sx={{
                          fontSize: 18, color: 'text.disabled',
                          transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none',
                        }} />
                      </TableCell>
                      <TableCell>{fmtDate(rx.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<PersonIcon fontSize="small" />}
                          onClick={e => { e.stopPropagation(); navigate(`/patients/${rx.patient_id}`); }}
                          sx={{ textTransform: 'none' }}
                        >
                          {rx.patient?.name || `Paciente #${rx.patient_id}`}
                        </Button>
                      </TableCell>
                      <TableCell>{doctorById(rx.doctor_id)?.name ? `Dr. ${doctorById(rx.doctor_id).name}` : '—'}</TableCell>
                      <TableCell>
                        <Chip label={st.label} size="small" sx={{ bgcolor: st.color, color: 'white', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{rx.items.length} med.</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {(rx.status === 'emitida' || rx.status === 'vencida') && (
                          <>
                            {features?.whatsapp && (
                              <Tooltip title="Enviar receta por WhatsApp">
                                <IconButton size="small" sx={{ color: '#25D366' }}
                                  onClick={e => { e.stopPropagation(); handleWhatsAppRx(rx); }}>
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Imprimir / descargar PDF">
                              <IconButton size="small"
                                onClick={e => { e.stopPropagation(); handlePdf(rx); }}>
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', py: 2 }}>
                          {rx.diagnosis_hint && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              <strong>Diagnóstico:</strong> {rx.diagnosis_hint}
                            </Typography>
                          )}
                          {rx.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              <strong>Notas:</strong> {rx.notes}
                            </Typography>
                          )}
                          <MedicationList items={rx.items} isMobile={false} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={-1}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelDisplayedRows={({ from, to }) => `${from}–${to}`}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default Recetas;
